import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Sparkles, ArrowLeft, Search, Loader2 } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import SiteFooter from '../components/SiteFooter';

const Encyclopedia = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSuit, setFilterSuit] = useState('all');
  const [selectedCard, setSelectedCard] = useState(null);

  useEffect(() => {
    fetchCards();
  }, []);

  useEffect(() => {
    let result = cards;
    if (filterSuit !== 'all') {
      result = result.filter((c) => c.suit === filterSuit);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((c) => c.name.toLowerCase().includes(q));
    }
    setFiltered(result);
  }, [cards, search, filterSuit]);

  const fetchCards = async () => {
    try {
      const response = await axios.get(`${API}/cards`);
      setCards(response.data.cards);
      setFiltered(response.data.cards);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const suits = [
    { id: 'all', label: 'All Cards' },
    { id: 'major', label: 'Major Arcana' },
    { id: 'wands', label: 'Wands' },
    { id: 'cups', label: 'Cups' },
    { id: 'swords', label: 'Swords' },
    { id: 'pentacles', label: 'Pentacles' },
  ];

  return (
    <div className="encyclopedia-page min-h-screen bg-[#050505] text-[#F8F9FA]">
      <nav className="glass-effect border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
            className="flex items-center gap-2 text-zinc-400 hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            <span>Dashboard</span>
          </button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="text-2xl font-heading font-light">Seatask Arcane AI</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12" data-testid="encyclopedia-page">
        <div className="mb-8">
          <h1 className="text-5xl font-heading font-light tracking-tighter mb-4">The Encyclopedia</h1>
          <p className="encyclopedia-muted text-lg">Browse all 78 cards of the Rider-Waite deck</p>
        </div>

        {/* Search & Filter */}
        <div className="glass-effect rounded-lg p-4 mb-8 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="encyclopedia-faint absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" strokeWidth={1.5} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search cards..."
              data-testid="encyclopedia-search"
              className="w-full pl-10 pr-4 py-2 bg-[#0F0F11] border border-white/10 rounded-md text-[#F8F9FA] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {suits.map((s) => (
              <button
                key={s.id}
                onClick={() => setFilterSuit(s.id)}
                data-testid={`filter-suit-${s.id}`}
                className={`px-4 py-2 rounded-md text-sm transition-all ${
                  filterSuit === s.id
                    ? 'bg-[#D4AF37] text-[#050505]'
                    : 'encyclopedia-filter-idle border border-white/10 hover:border-[#D4AF37]/50'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin" />
          </div>
        ) : (
          <>
            <p className="encyclopedia-faint text-sm mb-6">{filtered.length} cards</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filtered.map((card) => (
                <button
                  key={card.id}
                  onClick={() => setSelectedCard(card)}
                  data-testid={`encyclopedia-card-${card.id}`}
                  className="encyclopedia-card group rounded-lg overflow-hidden transition-all card-hover bg-[#0a0a14]"
                >
                  <div className="aspect-[7/12] bg-[#0a0a14] relative">
                    {card.image_url && (
                      <img
                        src={card.image_url}
                        alt={card.name}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className="px-2 py-2.5 bg-[#0a0a14] min-h-[3rem] flex items-center justify-center">
                    <p className="encyclopedia-card-title text-sm font-heading text-center leading-snug line-clamp-2">{card.name}</p>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Card Detail Modal */}
      {selectedCard && (
        <div
          className="fixed inset-0 z-50 bg-[#050505]/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setSelectedCard(null)}
          data-testid="card-detail-modal"
        >
          <div
            className="glass-effect rounded-lg max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-full md:w-1/3">
                <div className="aspect-[7/12] rounded-lg overflow-hidden relative bg-[#0a0a14]">
                  {selectedCard.image_url && (
                    <img
                      src={selectedCard.image_url}
                      alt={selectedCard.name}
                      className="w-full h-full object-contain"
                    />
                  )}
                </div>
              </div>
              <div className="flex-1">
                <p className="encyclopedia-accent text-xs tracking-[0.2em] uppercase mb-2">
                  {selectedCard.suit === 'major' ? 'Major Arcana' : selectedCard.suit.charAt(0).toUpperCase() + selectedCard.suit.slice(1)}
                </p>
                <h2 className="encyclopedia-card-title text-3xl font-heading font-medium mb-6">{selectedCard.name}</h2>

                <div className="mb-6">
                  <h3 className="encyclopedia-muted text-sm tracking-[0.2em] uppercase mb-2">Upright</h3>
                  <p className="text-zinc-300 leading-relaxed text-sm mb-3">{selectedCard.upright_meaning}</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedCard.upright.map((kw, i) => (
                      <span key={i} className="encyclopedia-chip-upright px-3 py-1 text-xs rounded-full">
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="encyclopedia-muted text-sm tracking-[0.2em] uppercase mb-2">Reversed</h3>
                  <p className="text-zinc-300 leading-relaxed text-sm mb-3">{selectedCard.reversed_meaning}</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedCard.reversed.map((kw, i) => (
                      <span key={i} className="encyclopedia-chip-reversed px-3 py-1 text-xs rounded-full">
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => setSelectedCard(null)}
                  data-testid="close-card-detail"
                  className="encyclopedia-action-button px-6 py-2 border rounded-md transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <SiteFooter />
    </div>
  );
};

export default Encyclopedia;
